import { Box } from "@radix-ui/themes";
import { useRef } from "react";
import { Stage, Layer } from "react-konva";
import Konva from "konva";
import { Stage as StageLib } from "konva/lib/Stage";
import { Layer as LayerLib } from "konva/lib/Layer";
import { useMeasure, useUpdateEffect } from "react-use";
import { twMerge } from "tailwind-merge";
import { Dimensions } from "@/shared";

const ASPECT_RATIO = 16 / 9;

type Canvas2DWrapperProps = {
  size: Dimensions;
  renderUnderlay?: React.ReactNode;
  renderVideo?: React.ReactNode;
  onResize: (dimensions: Dimensions) => void;
  onPointerDown?: (e: Konva.KonvaEventObject<PointerEvent>) => void;
  onWheel?: (evt: Konva.KonvaEventObject<WheelEvent>) => void;
};

export function Canvas2DWrapper({
  size,
  children,
  renderUnderlay,
  renderVideo,
  onResize: onCanvasResize,
  onPointerDown,
}: React.PropsWithChildren<Canvas2DWrapperProps>) {
  const stageRef = useRef<StageLib | null>(null);
  const imageLayer = useRef<LayerLib | null>(null);
  const [canvasRef, rect] = useMeasure<HTMLDivElement>();

  useUpdateEffect(() => {
    if (rect.width && rect.height && imageLayer.current) {
      const currentAspectRatio = rect.width / rect.height;
      let newWidth: number;
      let newHeight: number;

      if (currentAspectRatio > ASPECT_RATIO) {
        newWidth = rect.height * ASPECT_RATIO;
        newHeight = rect.height;
      } else {
        newWidth = rect.width;
        newHeight = rect.width / ASPECT_RATIO;
      }

      const threshold = 1;
      if (
        Math.abs(newWidth - size.width) > threshold ||
        Math.abs(newHeight - size.height) > threshold
      ) {
        onCanvasResize({
          width: newWidth,
          height: newHeight,
        });
      }

      imageLayer.current?.offsetY(0);
      imageLayer.current?.offsetX(0);
    }
  }, [rect.width, rect.height, onCanvasResize, size.width, size.height]);

  return (
    <Box
      ref={canvasRef}
      className={twMerge(["canvas-wrapper w-full h-full relative z-20"])}
      autoFocus
    >
      {renderUnderlay}
      {renderVideo}
      <Stage
        tabIndex={0}
        ref={stageRef}
        className={twMerge([
          "flex items-center justify-center absolute top-0 left-0 right-0 bottom-0 w-full h-full outline-none",
        ])}
        width={size.width}
        height={size.height}
        onContextMenu={(e) => {
          e.evt.preventDefault();
          return false;
        }}
        onPointerDown={onPointerDown}
      >
        <Layer ref={imageLayer}>{children}</Layer>
      </Stage>
    </Box>
  );
}
